import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import AssistantLayout from '../components/AssistantLayout'
import ConversationList from '../components/assistant/ConversationList'
import ChatPane from '../components/assistant/ChatPane'
import ContextBag from '../components/assistant/ContextBag'
import TokenLimitDialog from '../components/assistant/TokenLimitDialog'
import ToastContainer, { useToast } from '../components/ToastContainer'
import {
  sendMessage,
  getConversations,
  getConversation,
  deleteConversation,
  updateConversation,
  convertMessageToNote,
  appendMessageToNote,
} from '../api/agent'
import type { ConversationListItem, ConversationDetail } from '../api/agent'
import type { Tag } from '../api/tags'
import { getTags } from '../api/tags'

const CONV_PAGE_SIZE = 30

/**
 * Parse a 429 refresh timestamp from the backend error detail string:
 * "… Your tokens will be refreshed on January 01, 2026 at 00:00 UTC."
 * Returns epoch seconds or null.
 */
function parse429RefreshTime(detail: string): number | null {
  try {
    const match = detail.match(/refreshed on (.+?)\.$/)
    if (match) {
      const ts = Date.parse(match[1])
      if (!isNaN(ts)) return Math.floor(ts / 1000)
    }
  } catch { /* ignore */ }
  return null
}

// ── Inline title edit ──────────────────────────────────────────────────────────
function ConversationTitle({
  title,
  onSave,
}: {
  title: string
  onSave: (t: string) => Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(title)
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { setDraft(title) }, [title])
  useEffect(() => { if (editing) inputRef.current?.select() }, [editing])

  async function commit() {
    const trimmed = draft.trim()
    if (!trimmed || trimmed === title) { setEditing(false); setDraft(title); return }
    setSaving(true)
    try {
      await onSave(trimmed)
    } finally {
      setSaving(false)
      setEditing(false)
    }
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        data-testid="conv-title-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit()
          if (e.key === 'Escape') { setEditing(false); setDraft(title) }
        }}
        disabled={saving}
        className="text-sm font-semibold text-gray-900 bg-transparent border-b border-primary outline-none w-full max-w-md"
      />
    )
  }

  return (
    <button
      data-testid="conv-title-display"
      onDoubleClick={() => setEditing(true)}
      title="Double-click to rename"
      className="text-sm font-semibold text-gray-900 truncate hover:text-primary transition-colors text-left"
    >
      {title}
    </button>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

export default function AssistantPage() {
  const { conversationId: paramId } = useParams<{ conversationId?: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { toasts, addToast, dismissToast } = useToast()

  // ── Conversation list ──────────────────────────────────────────────────────
  const [conversations, setConversations] = useState<ConversationListItem[]>([])
  const [convLoading, setConvLoading] = useState(false)
  const [convHasMore, setConvHasMore] = useState(false)
  // Use a ref for skip so loadConversations callback never goes stale
  const convSkipRef = useRef(0)
  // Ref to always-current handleNewChat for the event listener (avoids stale closure)
  const handleNewChatRef = useRef<() => void>(() => {})
  const [convFilterTags, setConvFilterTags] = useState<Tag[]>([])
  const [availableTags, setAvailableTags] = useState<Tag[]>([])

  // ── Selected conversation ─────────────────────────────────────────────────
  const [selectedId, setSelectedId] = useState<string | null>(paramId ?? null)
  const [conversation, setConversation] = useState<ConversationDetail | null>(null)
  const [convDetailLoading, setConvDetailLoading] = useState(false)

  // ── Context bag ────────────────────────────────────────────────────────────
  const [mode, setMode] = useState<'smart_search' | 'manual'>('smart_search')
  const [pinnedNoteIds, setPinnedNoteIds] = useState<string[]>([])

  // ── Chat input ─────────────────────────────────────────────────────────────
  const [inputValue, setInputValue] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [optimisticUserMessage, setOptimisticUserMessage] = useState<string | null>(null)

  // ── Conversation length limit ─────────────────────────────────────────────
  // Kept in sync from: (a) conversation detail load, (b) SendMessageResponse
  const [messageCount, setMessageCount] = useState(0)
  const [atMessageLimit, setAtMessageLimit] = useState(false)

  // ── UI state ───────────────────────────────────────────────────────────────
  const [showTokenDialog, setShowTokenDialog] = useState(false)
  const [tokenRefreshTime, setTokenRefreshTime] = useState<number | null>(null)
  const [activePane, setActivePane] = useState<'history' | 'chat' | 'context'>('chat')

  // Token info from user
  const remainingTokens: number = (user as any)?.remaining_tokens ?? 999999
  const tokensRefreshTime: number | null = (user as any)?.tokens_refresh_time ?? null

  // ── Load tags on mount ─────────────────────────────────────────────────────
  useEffect(() => {
    getTags().then(setAvailableTags).catch(() => {})
  }, [])

  // ── Load conversations (stable callback, reset=true resets skip) ──────────
  const loadConversations = useCallback(async (reset: boolean) => {
    setConvLoading(true)
    if (reset) convSkipRef.current = 0
    const skip = convSkipRef.current
    try {
      const res = await getConversations(
        { selected_tags: convFilterTags.length > 0 ? convFilterTags : undefined },
        skip,
        CONV_PAGE_SIZE
      )
      setConversations((prev) => (reset ? res.data : [...prev, ...res.data]))
      convSkipRef.current = skip + res.data.length
      setConvHasMore(skip + res.data.length < res.count)
    } catch {
      // ignore
    } finally {
      setConvLoading(false)
    }
  }, [convFilterTags]) // convFilterTags is the only real dependency

  // Reload when filter changes
  useEffect(() => { loadConversations(true) }, [convFilterTags]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load conversation detail when selection changes ───────────────────────
  useEffect(() => {
    if (!selectedId) {
      setConversation(null)
      setMessageCount(0)
      setAtMessageLimit(false)
      return
    }
    setConvDetailLoading(true)
    getConversation(selectedId)
      .then((c) => {
        setConversation(c)
        setMode(c.mode)
        setPinnedNoteIds(c.pinned_note_ids ?? [])
        const count = c.message_count ?? c.messages?.length ?? 0
        setMessageCount(count)
        setAtMessageLimit(count >= 20)
      })
      .catch(() => {
        setConversation(null)
        addToast('Failed to load conversation', 'error')
      })
      .finally(() => setConvDetailLoading(false))
  }, [selectedId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync URL param → selectedId on navigation
  useEffect(() => {
    if (paramId && paramId !== selectedId) setSelectedId(paramId)
  }, [paramId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for "start new chat" event emitted from the conversation-limit banner.
  // Use a ref so the listener always calls the latest handleNewChat without needing
  // to re-register on every render.

  // ── Handlers ──────────────────────────────────────────────────────────────

  function handleSelect(id: string) {
    setSelectedId(id)
    navigate(`/assistant/${id}`, { replace: true })
    setActivePane('chat')
  }

  function handleNewChat() {
    setSelectedId(null)
    setConversation(null)
    setMode('smart_search')
    setPinnedNoteIds([])
    setInputValue('')
    setMessageCount(0)
    setAtMessageLimit(false)
    navigate('/assistant', { replace: true })
    setActivePane('chat')
  }

  // Keep the ref current after every render
  handleNewChatRef.current = handleNewChat

  useEffect(() => {
    const handler = () => handleNewChatRef.current()
    window.addEventListener('assistant:new-chat', handler)
    return () => window.removeEventListener('assistant:new-chat', handler)
  }, [])

  async function handleDelete(id: string) {
    try {
      await deleteConversation(id)
      setConversations((prev) => prev.filter((c) => c.id !== id))
      if (selectedId === id) handleNewChat()
      addToast('Conversation deleted', 'success')
    } catch {
      addToast('Failed to delete conversation', 'error')
    }
  }

  async function handleTitleSave(newTitle: string) {
    if (!selectedId) return
    try {
      await updateConversation(selectedId, { title: newTitle })
      setConversation((prev) => prev ? { ...prev, title: newTitle } : prev)
      setConversations((prev) =>
        prev.map((c) => (c.id === selectedId ? { ...c, title: newTitle } : c))
      )
    } catch {
      addToast('Failed to rename conversation', 'error')
      throw new Error('rename failed')
    }
  }

  async function handleModeChange(newMode: 'smart_search' | 'manual') {
    setMode(newMode)
    if (selectedId) {
      try {
        await updateConversation(selectedId, { mode: newMode })
      } catch {
        addToast('Failed to update mode', 'error')
      }
    }
  }

  async function handlePinnedNotesChange(ids: string[]) {
    setPinnedNoteIds(ids)
    if (selectedId) {
      try {
        await updateConversation(selectedId, { pinned_note_ids: ids })
      } catch {
        addToast('Failed to update pinned notes', 'error')
      }
    }
  }

  async function handleSend() {
    if (!inputValue.trim() || isSending) return
    if (remainingTokens <= 0) { setShowTokenDialog(true); return }
    if (atMessageLimit) return  // guard: banner button is the right CTA

    const question = inputValue.trim()
    setInputValue('')
    setIsSending(true)
    setOptimisticUserMessage(question)
    setActivePane('chat')

    try {
      const res = await sendMessage({
        question,
        conversation_id: selectedId ?? undefined,
        pinned_note_ids: pinnedNoteIds.length > 0 ? pinnedNoteIds : null,
        mode,
      })

      if (!selectedId) {
        setSelectedId(res.conversation_id)
        navigate(`/assistant/${res.conversation_id}`, { replace: true })
      }

      // Update limit counters from the response
      setMessageCount(res.message_count ?? 0)
      setAtMessageLimit(res.at_message_limit ?? false)

      // Refresh list and conversation detail
      loadConversations(true)
      const updated = await getConversation(res.conversation_id)
      setConversation(updated)
    } catch (err: any) {
      const status = err?.response?.status
      const detail: string = err?.response?.data?.detail ?? ''

      if (status === 429) {
        const parsed = parse429RefreshTime(detail)
        setTokenRefreshTime(parsed ?? tokensRefreshTime)
        setShowTokenDialog(true)
      } else if (status === 400 && detail.includes('maximum')) {
        // Conversation turn limit hit — update state and show inline banner
        setAtMessageLimit(true)
        addToast('This conversation has reached its turn limit. Start a new chat.', 'error')
      } else {
        addToast(detail || 'Failed to send message. Please try again.', 'error')
      }
      // Restore user input so they can retry
      setInputValue(question)
    } finally {
      setIsSending(false)
      setOptimisticUserMessage(null)
    }
  }

  async function handleConvertToNote(messageId: string) {
    if (!selectedId) return
    try {
      const res = await convertMessageToNote(selectedId, messageId)
      if (res?.note_id) {
        addToast('Note created! Opening…', 'success')
        setTimeout(() => navigate(`/notes/${res.note_id}`), 600)
      }
    } catch {
      addToast('Failed to create note', 'error')
    }
  }

  async function handleAppendToNote(convId: string, msgId: string, targetNoteId: string) {
    try {
      await appendMessageToNote(convId, msgId, targetNoteId)
      addToast('Response appended to note', 'success')
    } catch {
      addToast('Failed to append to note', 'error')
      throw new Error('append failed') // re-throw so dialog can show inline error
    }
  }

  // ── Computed ───────────────────────────────────────────────────────────────
  const activeTags = conversation?.tags ?? []
  const pinnedCount = pinnedNoteIds.length

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <AssistantLayout>
      {/* Three-pane workspace */}
      <div className="flex h-full overflow-hidden">

        {/* LEFT — Conversation history */}
        <aside
          data-testid="assistant-history-pane"
          className={`flex-shrink-0 border-r border-gray-200 bg-white flex flex-col w-72
            ${activePane === 'history' ? 'flex' : 'hidden'} lg:flex`}
        >
          <ConversationList
            conversations={conversations}
            selectedId={selectedId}
            isLoading={convLoading}
            hasMore={convHasMore}
            onSelect={handleSelect}
            onNewChat={handleNewChat}
            onDelete={handleDelete}
            onLoadMore={() => loadConversations(false)}
            filterTags={convFilterTags}
            onFilterTagsChange={(tags) => { setConvFilterTags(tags); convSkipRef.current = 0 }}
            availableTags={availableTags}
          />
        </aside>

        {/* CENTER — Chat */}
        <main
          data-testid="assistant-chat-pane"
          className={`flex-1 flex flex-col min-w-0 bg-gray-50 pb-14 lg:pb-0
            ${activePane === 'chat' ? 'flex' : 'hidden'} lg:flex`}
        >
          <ChatPane
            conversation={conversation}
            isLoadingConversation={convDetailLoading}
            isSending={isSending}
            optimisticUserMessage={optimisticUserMessage}
            remainingTokens={remainingTokens}
            tokensRefreshTime={tokensRefreshTime}
            inputValue={inputValue}
            onInputChange={setInputValue}
            onSend={handleSend}
            onTokenLimitHit={() => setShowTokenDialog(true)}
            onConvertToNote={handleConvertToNote}
            onAppendToNote={handleAppendToNote}
            onOpenContextBag={() => setActivePane('context')}
            pinnedNoteCount={pinnedCount}
            activeTags={activeTags}
            messageCount={messageCount}
            atMessageLimit={atMessageLimit}
            titleSlot={
              conversation ? (
                <ConversationTitle
                  title={conversation.title}
                  onSave={handleTitleSave}
                />
              ) : null
            }
          />
        </main>

        {/* RIGHT — Context Bag */}
        <aside
          data-testid="assistant-context-pane"
          className={`flex-shrink-0 border-l border-gray-200 bg-white flex flex-col w-80 pb-14 lg:pb-0
            ${activePane === 'context' ? 'flex' : 'hidden'} lg:flex`}
        >
          <ContextBag
            mode={mode}
            pinnedNoteIds={pinnedNoteIds}
            onModeChange={handleModeChange}
            onPinnedNotesChange={handlePinnedNotesChange}
            remainingTokens={remainingTokens}
            tokensRefreshTime={tokensRefreshTime}
          />
        </aside>
      </div>

      {/* Mobile bottom tab bar */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 border-t border-gray-200 bg-white z-30 flex safe-area-bottom">
        {(
          [
            {
              key: 'history' as const,
              label: 'History',
              badge: null,
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              ),
            },
            {
              key: 'chat' as const,
              label: 'Chat',
              badge: null,
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              ),
            },
            {
              key: 'context' as const,
              label: 'Context',
              badge: pinnedCount > 0 ? pinnedCount : null,
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              ),
            },
          ]
        ).map(({ key, label, icon, badge }) => (
          <button
            key={key}
            onClick={() => setActivePane(key)}
            className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-xs min-h-[52px] transition-colors ${
              activePane === key ? 'text-primary' : 'text-gray-500'
            }`}
          >
            {icon}
            <span>{label}</span>
            {badge !== null && (
              <span className="absolute top-1.5 right-1/4 translate-x-2 w-4 h-4 bg-primary text-white rounded-full text-[10px] flex items-center justify-center leading-none">
                {badge}
              </span>
            )}
          </button>
        ))}
      </nav>

      {/* Token limit dialog */}
      <TokenLimitDialog
        isOpen={showTokenDialog}
        refreshTime={tokenRefreshTime ?? tokensRefreshTime}
        onClose={() => setShowTokenDialog(false)}
      />

      {/* Toast notifications */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </AssistantLayout>
  )
}


